import React from 'react';

interface AppErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode | ((reset: () => void) => React.ReactNode);
}

interface AppErrorBoundaryState {
  hasError: boolean;
}

// Red de seguridad global: si algo revienta al renderizar (p. ej. el DOM reescrito
// por la traducción del navegador), mostramos un fallback en vez de pantalla en blanco.
class AppErrorBoundary extends React.Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  state: AppErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): AppErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('AppErrorBoundary caught an error', error, info.componentStack);
  }

  reset = (): void => this.setState({ hasError: false });

  render(): React.ReactNode {
    if (!this.state.hasError) return this.props.children;
    const { fallback } = this.props;
    if (typeof fallback === 'function') return fallback(this.reset);
    if (fallback !== undefined) return fallback;
    return (
      <div
        role="alert"
        className="min-h-screen flex flex-col items-center justify-center gap-4 bg-gray-100 px-4 text-center"
      >
        <h1 className="text-2xl font-bold text-gray-800">Something went wrong</h1>
        <p className="text-gray-600">The page hit an unexpected error. Reload to continue.</p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="rounded-md bg-gray-800 px-4 py-2 text-white hover:bg-gray-700"
        >
          Reload
        </button>
      </div>
    );
  }
}

export default AppErrorBoundary;
